import React from "react";
import PropTypes from "prop-types";
import ImmutablePropTypes from "react-immutable-proptypes";
import classNames from "classnames";
import { Client, Language } from "resourceModels";

import Button from "components/Common/Button";
import dialogWrapper from "components/Common/Dialog";
import InfoTooltip from "components/Common/InfoTooltip";
import colors from "stylesheets/utilities/colors.scss";
import MultilingualInputLanguagesDialog from "./MultilingualInputLanguagesDialog";

import "./style.scss";

const EnhancedIntrosEditorLanguagesDialog = dialogWrapper(MultilingualInputLanguagesDialog);

/**
 * @param {Boolean} bodyTooLong
 * @param {Boolean} noBody
 * @param {Boolean} invalid
 * @param {Boolean} isDialogOpen
 * @returns {Array.<String>}
 */
function styleIcon(bodyTooLong, noBody, invalid, isDialogOpen) {
  let icon = "Globe";
  let iconColor = "";

  if (noBody) {
    icon = "";
  } else if (bodyTooLong) {
    icon = "Warning";
  }

  if (invalid) {
    iconColor = colors.colorUIWarn;
  } else if (isDialogOpen) {
    iconColor = colors.colorUIPrimary;
  }

  return [icon, iconColor];
}

const BLOCK_NAME = "MultilingualInputLanguageButton";

export default class MultilingualInputLanguageButton extends React.Component {
  handleToggleLanguagesDialogBound = this.handleToggleLanguagesDialog.bind(this);

  state = {
    isLanguageDialogOpen: false
  };

  /**
   */
  handleToggleLanguagesDialog() {
    this.setState(prevState => ({
      isLanguageDialogOpen: !prevState.isLanguageDialogOpen
    }));
  }

  /**
   * @param {String} fieldName
   * @returns {Boolean}
   */
  hasInvalidField(fieldName) {
    const { invalidFields } = this.props;

    return invalidFields.includes(fieldName);
  }

  /**
   * @returns {ReactElement}
   */
  render() {
    const {
      /** @type Client */
      client,
      activeLanguage,
      languages,
      availableLanguages,
      enabledLanguages,
      invalidFields,
      addLanguageAction,
      onClick,
      deleteLanguage
    } = this.props;

    const { isLanguageDialogOpen } = this.state;

    const hidden = (
      availableLanguages.size === 0 && (
        languages.size - 1 === client.translatedLanguages.size
      )
    );
    const noBody = this.hasInvalidField("bodyMissing");
    const bodyTooLong = this.hasInvalidField("bodyLength");
    const invalid = noBody || bodyTooLong;
    const [icon, iconColor] = styleIcon(bodyTooLong, noBody, invalid, isLanguageDialogOpen);

    return (
      <div
        className={classNames(BLOCK_NAME, {
          [`${BLOCK_NAME}--hidden`]: hidden
        })}
      >
        {noBody && (
          <InfoTooltip
            customClassName={`${BLOCK_NAME}--invalid-tooltip Tooltip--Warning`}
            blurb="One or more translations are missing content."
            iconDefault="Warning"
            fillColor={colors.colorUIWarn}
            absolute
            inModal
            iconHeight={18}
          />
        )}
        <Button
          text={enabledLanguages.find(language => language.code === activeLanguage).englishName}
          title="Current Language"
          size="small"
          icon={icon}
          secondIcon="ChevronDown"
          customClassName={classNames(`${BLOCK_NAME}__current-language`, {
            [`${BLOCK_NAME}--active`]: isLanguageDialogOpen
          })}
          fillColor={isLanguageDialogOpen ? colors.colorUIPrimary : ""}
          iconColor={iconColor}
          light
          onClick={this.handleToggleLanguagesDialogBound}
        />
        <EnhancedIntrosEditorLanguagesDialog
          closeDialog={() => this.setState({ isLanguageDialogOpen: false })}
          availableLanguages={availableLanguages}
          invalidFields={invalidFields}
          activeLanguage={activeLanguage}
          onLanguageClick={addLanguageAction}
          onClick={onClick}
          isOpen={isLanguageDialogOpen}
          languages={languages}
          client={client}
          enabledLanguages={enabledLanguages}
          deleteLanguage={deleteLanguage}
          alignment="right"
          width={216}
          customClassName={`${BLOCK_NAME}__dialog`}
        />
      </div>
    );
  }
}

MultilingualInputLanguageButton.propTypes = {
  activeLanguage: PropTypes.string.isRequired,
  availableLanguages: ImmutablePropTypes.list.isRequired,
  languages: ImmutablePropTypes.listOf(PropTypes.instanceOf(Language)).isRequired,
  enabledLanguages: ImmutablePropTypes.listOf(PropTypes.instanceOf(Language)).isRequired,
  invalidFields: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  client: PropTypes.instanceOf(Client).isRequired,
  addLanguageAction: PropTypes.func.isRequired,
  deleteLanguage: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired
};
